import type { MseProduct } from '@/types/home/product';
import tech_qa_sameple1 from '@/assets/img/techqa/Sample Image 1.png';
import tech_qa_sameple2 from '@/assets/img/techqa/Sample Image 2.png';
import tech_qa_sameple3 from '@/assets/img/techqa/Sample Image 3.png';

type languages = 'en' | 'cn' | 'tw' | 'jp';
export type ExampleItem = {
  preText?: string;
  text: string;
};

export type FileExampleItem = {
  text: string;
  imgLink: string;
  fileLink: string;
};

export const EXAMPLES: Partial<Record<languages, Partial<Record<MseProduct, ExampleItem[]>>>> = {
  en: {
    TECHQA: [
      {
        preText: 'Understand',
        text: 'Please help me understand the latest developments in solid-state battery technology',
      },
      {
        preText: 'Solve',
        text: 'Rail bogie material with strong vibration damping, fatigue resistance, and fire safety',
      },
      {
        preText: 'Scout',
        text: 'Benchmarking CATL and LG Chem Solid-State Battery Patents',
      },
      {
        preText: 'Apply',
        text: 'Main applications and products of graphene-based coatings by companies',
      },
    ],
    FIND_SOLUTIONS: [
      {
        text: 'How to improve the adhesion and durability of waterborne polyurethane coatings in high humidity environments, while reducing the emission of volatile organic compounds (VOCs)?',
      },
      {
        text: 'How to improve the UVA protection effect of zinc oxide sunscreen without affecting stability in high temperature and high humidity environments, while avoiding greasy residue?',
      },
      {
        text: 'How to improve the oxidation and corrosion resistance of nickel-based alloys at temperatures above 1000°C?',
      },
    ],
    FORMULATION: [
      {
        text: 'Search for relevant prior-art formulations for a lightweight gel-cream moisturizer for oily, acne-prone skin using panthenol as a required soothing agent.',
      },
      {
        text: 'Formulate a structural adhesive for cell-to-pack bonding in EV batteries. It must achieve a thermal conductivity of >2.0 W/m·K using non-abrasive fillers and possess a dielectric strength of >15 kV/mm. It must maintain a thixotropic index suitable for high-speed robotic bead application without slumping on vertical surfaces.',
      },
      {
        text: 'Create a synthesis protocol for the chemical recycling of rigid PU foam waste (e.g., from end-of-life refrigerators) using a split-phase glycolysis approach.',
      }
    ],
    FORMULATION_LEGACY: [
      {
        text: 'I want to develop a sunscreen with SPF 50 and water resistance. I want to include zinc oxide and glycerin.',
      },
      {
        text: 'I need a rubber formulation for explosion-proof tires, and I want to include carbon black, sulfur and stearic acid.',
      },
    ],
    DFMEA_ASSISTANT: [
      {
        text: 'Using (selective microwave heating) technology, microwave energy is concentrated in the center of the bun by controlling the microwave frequency and polarization. Additionally, the heating process is divided into multiple short cycles, with a short pause after each cycle to allow the heat to evenly diffuse within the bun, preventing localized overheating. This method helps in uniform heating and reduces moisture loss at the edges.',
      },
    ],
  },
  cn: {
    TECHQA: [
      {
        preText: '懂技术',
        text: '请帮我了解固态电池技术的最新进展',
      },
      {
        preText: '解难题',
        text: '不含CPFAS的高性能超疏水涂层的开发',
      },
      {
        preText: '看产业',
        text: '对宁德时代和LG化学固态电池专利进行基准测试',
      },
      {
        preText: '落产品',
        text: '各公司石墨烯基涂层的主要应用和产品',
      },
    ],
    FIND_SOLUTIONS: [
      {
        text: '如何在高湿度环境下改善水性聚氨酯涂料的附着力和耐久性，同时减少挥发性有机化合物(VOC)的排放？',
      },
      {
        text: '如何在不影响高温高湿环境下稳定性的前提下，改善氧化锌防晒霜的UVA防护效果，同时避免油腻残留？',
      },
      {
        text: '如何提高镍基合金在1000°C以上温度下的抗氧化和抗腐蚀能力？',
      },
    ],
    DFMEA_ASSISTANT: [
      {
        text: '采用(选择性微波加热)技术，通过控制微波频率和极化，使微波能量集中于馒头中心部分。同时，加热过程分为多个短周期，每个周期后进行短暂停顿，让热量在馒头内部均匀扩散，避免局部过热。这种方法有助于均匀加热，减少边缘水分流失。',
      },
    ],
    FORMULATION: [
      {
        text: '搜索含有泛醇（必需舒缓成分）、适用于油性及易生粉刺皮肤的轻质啫喱面霜保湿剂的相关现有配方。',
      },
      {
        text: '开发一种用于电动汽车电池 CTP（无模组）粘接的结构胶。需使用非磨蚀性填料实现 >2.0 W/m·K 的导热率，且介电强度 >15 kV/mm。需保持良好的触变指数，以适应高速机器人涂胶且在垂直面上不流挂。',
      },
      {
        text: '制定针对废旧冰箱等硬质聚氨酯（PU）泡沫废料的化学回收合成方案，采用分相醇解法（Split-phase glycolysis）。',
      }
    ],
    FORMULATION_LEGACY: [
      {
        text: '我想开发一款具有 SPF 50 和防水效果的防晒霜，希望加入氧化锌和甘油。',
      },
      {
        text: '我需要一款防爆轮胎用橡胶配方，希望包含炭黑、硫磺和硬脂酸。',
      },
    ],
  },
  tw: {
    TECHQA: [
      {
        preText: '懂技術',
        text: '請幫我了解固態電池技術的最新進展',
      },
      {
        preText: '解難題',
        text: '不含CPFAS的高性能超疏水塗層的開發',
      },
      {
        preText: '看產業',
        text: '對寧德時代和LG化學固態電池專利進行基準測試',
      },
      {
        preText: '落產品',
        text: '各公司石墨烯基塗層的主要應用和產品',
      },
    ],
    FIND_SOLUTIONS: [
      {
        text: '如何在高度濕環境下改善水性聚氨酯塗料的附著力和耐久性，同時減少揮發性有機化合物(VOC)的排放？',
      },
      {
        text: '如何在不影響高溫高濕環境下穩定性的前提下，改善氧化鋅防曬霜的UVA防護效果，同時避免油膩殘留？',
      },
      {
        text: '如何提高鎳基合金在1000°C以上溫度下的抗氧化和抗腐蝕能力？',
      },
    ],
    DFMEA_ASSISTANT: [
      {
        text: '採用(選擇性微波加熱)技術，透過控制微波頻率和極化，使微波能量集中於饅頭中心部分。同時，加熱過程分為多個短週期，每個週期後進行短暫停頓，讓熱量在饅頭內部均勻擴散，避免局部過熱。這種方法有助於均勻加熱，減少邊緣水分流失。',
      },
    ],
    FORMULATION: [
      {
        text: '搜尋含有泛醇（必需舒緩成分）、適用於油性及易生粉刺皮膚的輕質啫喱面霜保濕劑之相關現有配方。',
      },
      {
        text: '開發一種用於電動汽車電池 CTP（無模組）粘接的結構膠。需使用非磨蝕性填料實現 >2.0 W/m·K 的導熱率，且介電強度 >15 kV/mm。需保持良好的觸變指數，以適應高速機器人塗膠且在垂直面上不流掛。',
      },
      {
        text: '針對廢舊冰箱等硬質聚氨酯（PU）泡沫廢料，制定採用分相醇解法（Split-phase glycolysis）的化學回收合成方案。',
      }
    ],
  },
  jp: {
    TECHQA: [
      {
        preText: '理解',
        text: '固体電池技術の最新動向を把握するのを手伝ってください。',
      },
      {
        preText: '解決',
        text: '振動減衰性、耐疲労性、耐火性に優れた鉄道台車用材料',
      },
      {
        preText: '調査',
        text: '寧德時代（CATL）とLG化学の固体電池特許のベンチマーキング',
      },
      {
        preText: '応用',
        text: '各社のグラフェン系コーティングの主な応用と製品',
      },
    ],
    FORMULATION: [
      {
        text: 'パンテノールを必須の整肌成分とし、脂性肌・ニキビ肌に適した軽量なジェルクリーム状保湿剤の既存処方を検索。',
      },
      {
        text: 'EVバッテリーのセル・トゥ・パック（CTP）接合用構造用接着剤の処方を策定。非研磨性フィラーを用いて熱伝導率 2.0 W/m·K 以上、絶縁破壊強度 15 kV/mm 以上を実現し、垂直面で垂れのない高速ロボット塗布に適したチクソ性を維持すること。',
      },
      {
        text: '廃冷蔵庫等の硬質ポリウレタンフォーム廃棄物を対象とした、スプリットフェーズ（分相）グリコリス法による化学リサイクルの合成プロトコルを作成。',
      }
    ],
  },
};

export const FILE_EXAMPLES: Partial<
  Record<languages, Partial<Record<MseProduct, FileExampleItem[]>>>
> = {
  en: {
    TECHQA: [
      {
        text: 'Help me understand the latest developments for this semiconductor device',
        imgLink: tech_qa_sameple1,
        fileLink: tech_qa_sameple1,
      },
      {
        text: 'Main applications and products by companies for molecule of this structure',
        imgLink: tech_qa_sameple2,
        fileLink: tech_qa_sameple2,
      },
      {
        text: 'Help me find materials that can satisfy the property behaviour',
        imgLink: tech_qa_sameple3,
        fileLink: tech_qa_sameple3,
      },
    ],
  },
  cn: {
    TECHQA: [
      {
        text: '分析这个半导体器件的技术',
        imgLink: tech_qa_sameple1,
        fileLink: tech_qa_sameple1,
      },
      {
        text: '该结构分子的主要应用及相关产品',
        imgLink: tech_qa_sameple2,
        fileLink: tech_qa_sameple2,
      },
      {
        text: '请帮我找到能够满足性能要求的材料',
        imgLink: tech_qa_sameple3,
        fileLink: tech_qa_sameple3,
      },
    ],
  },
  tw: {
    TECHQA: [
      {
        text: '分析這個半導體器件的技術',
        imgLink: tech_qa_sameple1,
        fileLink: tech_qa_sameple1,
      },
      {
        text: '該結構分子的主要應用及相關產品',
        imgLink: tech_qa_sameple2,
        fileLink: tech_qa_sameple2,
      },
      {
        text: '請幫我找到能夠滿足性能要求的材料',
        imgLink: tech_qa_sameple3,
        fileLink: tech_qa_sameple3,
      },
    ],
  },
  jp: {
    TECHQA: [
      {
        text: 'この半導体デバイスの最新動向を理解するのを手伝ってください',
        imgLink: tech_qa_sameple1,
        fileLink: tech_qa_sameple1,
      },
      {
        text: 'この構造の分子に関する各社の主な応用と製品',
        imgLink: tech_qa_sameple2,
        fileLink: tech_qa_sameple2,
      },
      {
        text: 'この特性挙動を満たす材料を探すのを手伝ってください',
        imgLink: tech_qa_sameple3,
        fileLink: tech_qa_sameple3,
      },
    ],
  },
};
